import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { db } from "./firebaseConfig";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  updateDoc,
} from "firebase/firestore";
import "./StudentProfile.css";

const StudentProfile = ({ user }) => {
  const { studentId } = useParams();
  const navigate = useNavigate();
  
  // State variables
  const [student, setStudent] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [quizzes, setQuizzes] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [extraTimeInput, setExtraTimeInput] = useState(0);
  const [updateSuccess, setUpdateSuccess] = useState(false); // For success message
  const [teacherQuizIds, setTeacherQuizIds] = useState(new Set()); // Store teacher's quiz IDs
  const [stats, setStats] = useState({
    totalQuizzes: 0,
    averageScore: 0,
    highestScore: 0,
    lowestScore: 100,
    totalSubmissions: 0,
    recentActivity: null,
  });
  const [expandedQuizCode, setExpandedQuizCode] = useState(null);
  const [darkMode, setDarkMode] = useState(() => {
    // Get dark mode preference from localStorage or default to false
    const savedMode = localStorage.getItem("darkMode");
    return savedMode ? JSON.parse(savedMode) : false;
  });

  // Check if user is logged in and is a teacher or admin
  useEffect(() => {
    if (!user) {
      navigate("/");
    } else if (user.role !== "teacher" && user.role !== "admin") {
      navigate("/dashboard");
    }
  }, [user, navigate]);

  // Fetch student data with optimized batch queries
  useEffect(() => {
    const fetchStudentData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch student profile
        const studentDocRef = doc(db, "users", studentId);
        const studentDoc = await getDoc(studentDocRef);
        
        if (!studentDoc.exists()) {
          throw new Error("Student profile not found");
        }
        
        const studentData = studentDoc.data();
        setStudent({
          id: studentDoc.id,
          displayName: studentData.displayName || studentData.username || "Unknown",
          email: studentData.email || "No email",
          createdAt: studentData.createdAt ? studentData.createdAt.toDate() : new Date(),
          teacherCodes: studentData.teacherCodes || [],
          extraTimePercentage: studentData.extraTimePercentage || 0,
        });
        
        // Initialize the extra time input with the student's current value
        setExtraTimeInput(studentData.extraTimePercentage || 0);

        // Fetch quizzes created by the current teacher
        const quizzesRef = collection(db, "quizzes");
        const teacherQuizzesQuery = query(quizzesRef, where("creatorUid", "==", user.uid));
        const teacherQuizzesSnapshot = await getDocs(teacherQuizzesQuery);
        
        // Create a Set of quiz IDs for efficient lookup
        const teacherQuizIdsSet = new Set();
        teacherQuizzesSnapshot.docs.forEach(doc => {
          const quizData = doc.data();
          // Store both the document ID and the quizCode (if different)
          teacherQuizIdsSet.add(doc.id);
          if (quizData.quizCode) teacherQuizIdsSet.add(quizData.quizCode);
        });
        
        setTeacherQuizIds(teacherQuizIdsSet);

        // Fetch student submissions
        const subsRef = collection(db, "submissions");
        const q = query(subsRef, where("userId", "==", studentId));
        const snapshot = await getDocs(q);

        // Collect all question IDs to fetch in batch
        const questionIds = [];
        const submissionsWithoutQuestions = [];
        
        snapshot.docs.forEach(docSnap => {
          const data = docSnap.data();
          if (!data.questionId) return; // skip incomplete data
          
          const questionId = data.questionId.includes("/")
            ? data.questionId.split("/").pop() // handle "questions/abc123" style
            : data.questionId;
          
          questionIds.push(questionId);
          submissionsWithoutQuestions.push({
            id: docSnap.id,
            ...data,
            questionId: questionId,
            submittedAt: data.submittedAt?.toDate() || new Date(),
          });
        });
        
        // Batch fetch all questions at once
        const questionDocs = await Promise.all(
          questionIds.map(id => getDoc(doc(db, "questions", id)))
        );
        
        // Create a map of question data for quick lookup
        const questionDataMap = {};
        questionDocs.forEach(doc => {
          if (doc.exists()) {
            questionDataMap[doc.id] = doc.data();
          }
        });
        
        // Process submissions with question data
        const submissionsData = [];
        const quizzesData = {};
        
        submissionsWithoutQuestions.forEach(sub => {
          const questionData = questionDataMap[sub.questionId] || {};
          
          if (Object.keys(questionData).length === 0) return; // Skip if question data not found
          
          // Track unique quizzes
          if (sub.quizCode && !quizzesData[sub.quizCode]) {
            quizzesData[sub.quizCode] = {
              code: sub.quizCode,
              totalMarks: 0,
              maxMarks: 0,
              submissions: 0,
              lastSubmitted: sub.submittedAt || new Date(),
            };
          }

          // Update quiz stats
          if (sub.quizCode) {
            const markAwarded = parseFloat(sub.markAwarded) || 0;
            const maxMark = parseFloat(questionData.maxMark) || 0;
            
            quizzesData[sub.quizCode].totalMarks += markAwarded;
            quizzesData[sub.quizCode].maxMarks += maxMark;
            quizzesData[sub.quizCode].submissions += 1;
            
            // Update last submission date if newer
            if (sub.submittedAt > quizzesData[sub.quizCode].lastSubmitted) {
              quizzesData[sub.quizCode].lastSubmitted = sub.submittedAt;
            }
          }

          submissionsData.push({
            id: sub.id,
            quizCode: sub.quizCode || "NoCode",
            questionText: questionData.questionText || "Unknown Question",
            questionImageURL: questionData.questionImageURL || null,
            userAnswer: sub.userAnswer,
            markSchemeAnswer: questionData.markSchemeAnswer || "N/A",
            feedback: sub.feedback || "Pending",
            maxMark: questionData.maxMark || 0,
            markAwarded: sub.markAwarded ?? "Pending",
            submittedAt: sub.submittedAt,
            aiAnalysis: sub.aiAnalysis || null,
          });
        });

        // For admins, show all quizzes. For teachers, only show their own quizzes
        let filteredSubmissionsData = submissionsData;
        let filteredQuizzesData = quizzesData;
        
        // If user is a teacher (not admin), filter to only show their quizzes
        if (user.role === "teacher") {
          filteredSubmissionsData = submissionsData.filter(submission => 
            teacherQuizIdsSet.has(submission.quizCode)
          );
          
          // Filter quizzes to only include those created by the teacher
          filteredQuizzesData = {};
          Object.keys(quizzesData).forEach(quizCode => {
            if (teacherQuizIdsSet.has(quizCode)) {
              filteredQuizzesData[quizCode] = quizzesData[quizCode];
            }
          });
        }

        setSubmissions(filteredSubmissionsData);
        setQuizzes(filteredQuizzesData);
        
        // Calculate overall statistics
        if (filteredSubmissionsData.length > 0) {
          const quizCodes = Object.keys(filteredQuizzesData);
          let totalScore = 0;
          let totalMaxScore = 0;
          let highestQuizScore = 0;
          let lowestQuizScore = 100;
          let mostRecentActivity = new Date(0);
          
          // Calculate quiz-level statistics
          quizCodes.forEach(code => {
            const quiz = filteredQuizzesData[code];
            const quizPercentage = (quiz.totalMarks / quiz.maxMarks) * 100;
            
            totalScore += quiz.totalMarks;
            totalMaxScore += quiz.maxMarks;
            
            highestQuizScore = Math.max(highestQuizScore, quizPercentage);
            lowestQuizScore = Math.min(lowestQuizScore, quizPercentage);
            
            if (quiz.lastSubmitted > mostRecentActivity) {
              mostRecentActivity = quiz.lastSubmitted;
            }
          });
          
          setStats({
            totalQuizzes: quizCodes.length,
            averageScore: totalMaxScore > 0 ? ((totalScore / totalMaxScore) * 100).toFixed(2) : 0,
            highestScore: highestQuizScore.toFixed(2),
            lowestScore: lowestQuizScore === 100 && quizCodes.length === 0 ? 0 : lowestQuizScore.toFixed(2),
            totalSubmissions: filteredSubmissionsData.length,
            recentActivity: mostRecentActivity,
          });
        } else {
          // Set empty statistics when no submissions match
          setStats({
            totalQuizzes: 0,
            averageScore: 0,
            highestScore: 0,
            lowestScore: 0,
            totalSubmissions: 0,
            recentActivity: null,
          });
        }
        
        setLoading(false);
      } catch (error) {
        console.error("Error fetching student data:", error);
        setError(error.message || "Error fetching student data");
        setLoading(false);
      }
    };

    if (studentId) {
      fetchStudentData();
    }
  }, [studentId, navigate]);

  // Handle updating extra time percentage
  const handleUpdateExtraTime = async (newExtraTime) => {
    try {
      await updateDoc(doc(db, "users", studentId), { extraTimePercentage: newExtraTime });
      setUpdateSuccess(true);
      
      // Reset success message after 3 seconds
      setTimeout(() => {
        setUpdateSuccess(false);
      }, 3000);
      
      // Update local state to reflect changes
      setStudent(prev => ({
        ...prev,
        extraTimePercentage: newExtraTime
      }));
    } catch (error) {
      console.error("Error updating extra time:", error.message);
      alert("Failed to update extra time. Please try again.");
    }
  };

  // Toggle expanded quiz
  const toggleExpand = (quizCode) => {
    setExpandedQuizCode(prev => prev === quizCode ? null : quizCode);
  };

  // Format date for display
  const formatDate = (date) => {
    return new Date(date).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Helper function to get score class
  const getScoreClass = (percentage) => {
    if (percentage >= 80) return 'high';
    if (percentage >= 60) return 'medium';
    return 'low';
  };

  // Helper function to format feedback text with line breaks
  const formatFeedback = (feedback) => {
    if (feedback === "Pending") return feedback;
    
    // Format the feedback with better spacing
    const formattedFeedback = feedback
      .replace(/\.\s+/g, '.\n\n') // Add double line breaks after periods
      .replace(/:\s+/g, ':\n') // Add line breaks after colons
      .replace(/Score:\s+/g, '\nScore: '); // Ensure Score is on its own line
      
    return formattedFeedback;
  };

  // Extract common themes from AI feedback
  const extractInsights = (submissions) => {
    // Skip if no submissions or all are pending
    if (!submissions.length || submissions.every(s => s.feedback === "Pending")) {
      return { strengths: [], weaknesses: [], suggestions: [] };
    }
    
    // Simple analysis based on scores and feedback
    const strengths = [];
    const weaknesses = [];
    const suggestions = [];
    
    // Count high and low scoring submissions
    const highScoring = submissions.filter(s => {
      if (s.markAwarded === "Pending" || s.maxMark === 0) return false;
      return (parseFloat(s.markAwarded) / parseFloat(s.maxMark)) >= 0.8;
    });
    
    const lowScoring = submissions.filter(s => {
      if (s.markAwarded === "Pending" || s.maxMark === 0) return false;
      return (parseFloat(s.markAwarded) / parseFloat(s.maxMark)) < 0.6;
    });
    
    // Add insights based on score patterns
    if (highScoring.length > 0 && highScoring.length >= submissions.length * 0.3) {
      strengths.push("Consistently scores well on assessments");
    }
    
    if (lowScoring.length > 0 && lowScoring.length >= submissions.length * 0.3) {
      weaknesses.push("Struggles with some assessment questions");
      suggestions.push("Consider providing additional support materials");
    }
    
    // Look for keywords in feedback
    const allFeedback = submissions
      .filter(s => s.feedback !== "Pending")
      .map(s => s.feedback.toLowerCase());
    
    // Check for positive indicators
    if (allFeedback.some(f => f.includes("excellent") || f.includes("outstanding") || f.includes("very good"))) {
      strengths.push("Receives positive feedback on work quality");
    }
    
    // Check for improvement areas
    if (allFeedback.some(f => f.includes("improve") || f.includes("could have") || f.includes("should have"))) {
      suggestions.push("Review feedback for specific improvement areas");
    }
    
    // Check for understanding issues
    if (allFeedback.some(f => f.includes("misunderstood") || f.includes("confusion") || f.includes("unclear"))) {
      weaknesses.push("May have difficulty understanding some concepts");
      suggestions.push("Check for comprehension during lessons");
    }
    
    return {
      strengths: strengths.length ? strengths : ["Not enough data to determine strengths"],
      weaknesses: weaknesses.length ? weaknesses : ["Not enough data to determine areas for improvement"],
      suggestions: suggestions.length ? suggestions : ["Continue monitoring performance"],
    };
  };

  // Group submissions by quiz - memoized to prevent recalculation on every render
  const submissionsByQuiz = useMemo(() => {
    return submissions.reduce((acc, sub) => {
      if (!acc[sub.quizCode]) {
        acc[sub.quizCode] = [];
      }
      acc[sub.quizCode].push(sub);
      return acc;
    }, {});
  }, [submissions]);

  // Get insights from all submissions - memoized
  const insights = useMemo(() => extractInsights(submissions), [submissions]);

  // Generate CSV data for download
  const generateCSV = () => {
    if (submissions.length === 0) {
      return "";
    }
    
    // CSV header
    let csvContent = "Quiz Title,Percentage Score\n";
    
    // Group submissions by quiz for the CSV
    const submissionsByQuiz = {};
    submissions.forEach(sub => {
      if (!submissionsByQuiz[sub.quizCode]) {
        submissionsByQuiz[sub.quizCode] = {
          totalMarks: 0,
          maxMarks: 0,
          count: 0
        };
      }
      
      if (sub.markAwarded !== "Pending") {
        const markAwarded = parseFloat(sub.markAwarded);
        const maxMark = parseFloat(sub.maxMark);
        
        submissionsByQuiz[sub.quizCode].totalMarks += markAwarded;
        submissionsByQuiz[sub.quizCode].maxMarks += maxMark;
        submissionsByQuiz[sub.quizCode].count++;
      }
    });
    
    // Calculate total for average
    let overallTotalMarks = 0;
    let overallMaxMarks = 0;
    
    // Add each quiz as a row
    Object.keys(submissionsByQuiz).forEach(quizCode => {
      const quizData = submissionsByQuiz[quizCode];
      
      if (quizData.count > 0) {
        const quizTitle = quizCode; // Using quizCode as the title
        const percentage = quizData.maxMarks > 0 
          ? ((quizData.totalMarks / quizData.maxMarks) * 100).toFixed(2) + "%" 
          : "N/A";
        
        // Add to overall totals for average calculation
        overallTotalMarks += quizData.totalMarks;
        overallMaxMarks += quizData.maxMarks;
        
        // Create CSV row
        const row = [
          `"${quizTitle}"`,
          percentage
        ].join(",");
        
        csvContent += row + "\n";
      }
    });
    
    // Add average score as final row
    if (overallMaxMarks > 0) {
      const averagePercentage = ((overallTotalMarks / overallMaxMarks) * 100).toFixed(2) + "%";
      csvContent += `"Average Score",${averagePercentage}\n`;
    }
    
    return csvContent;
  };

  // Handle CSV download
  const handleDownloadCSV = () => {
    if (submissions.length === 0) {
      alert("No assessment data available to download.");
      return;
    }
    
    const csvData = generateCSV();
    const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    
    // Create a temporary link and trigger download
    const link = document.createElement("a");
    const fileName = `${student.displayName.replace(/\s+/g, "_")}_assessments_${new Date().toISOString().split("T")[0]}.csv`;
    
    link.setAttribute("href", url);
    link.setAttribute("download", fileName);
    link.style.display = "none";
    
    document.body.appendChild(link);
    link.click();
    
    // Clean up
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className={`student-profile-container ${darkMode ? 'dark-mode' : ''}`}>
      <div className="profile-content">
        {/* Header */}
        <div className="improved-header">
          <div className="nav-left">
            <div className="logo-container">
              <img src="/newlogo.png" alt="Logo" />
              <h1>Student Profile</h1>
            </div>
          </div>
          <div className="nav-right">
            <button onClick={() => navigate('/dashboard')} className="nav-button">
              Dashboard
            </button>
            <button onClick={() => navigate('/view-students')} className="nav-button">
              View Students
            </button>
            <button onClick={() => navigate(-1)} className="back-button">
              Back
            </button>
          </div>
        </div>

        {/* Loading state */}
        {loading && (
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading student profile...</p>
          </div>
        )}
        
        {/* Error state */}
        {error && !loading && (
          <div className="error-message">
            {error}
          </div>
        )}
        
        {/* Student profile */}
        {!loading && !error && student && (
          <div className="profile-sections">
            {/* Basic Info Card */}
            <div className="profile-card basic-info">
              <div className="profile-avatar">
                {student.displayName.charAt(0).toUpperCase()}
              </div>
              <div className="profile-details">
                <h2 className="student-name">{student.displayName}</h2>
                <p className="student-email">{student.email}</p>
                <p className="student-joined">Joined: {formatDate(student.createdAt)}</p>
                
                {/* Extra Time Input - Only visible to teachers and admins */}
                {(user?.role === "teacher" || user?.role === "admin") && (
                  <div className="extra-time-container">
                    <div className="extra-time-header">
                      <span className="extra-time-icon">⏱️</span>
                      <div className="extra-time-label">Extra Time Allowance</div>
                    </div>
                    <div className="extra-time-description">
                      Specify additional time (as a percentage) that this student should receive on timed assessments.
                    </div>
                    <div className="extra-time-input-group">
                      <div className="extra-time-input-wrapper">
                        <input
                          type="number"
                          min="0"
                          max="200"
                          value={extraTimeInput}
                          onChange={(e) => setExtraTimeInput(Number(e.target.value))}
                          className="extra-time-input"
                          aria-label="Extra time percentage"
                        />
                        <span className="extra-time-symbol">%</span>
                      </div>
                      <button
                        onClick={() => handleUpdateExtraTime(Number(extraTimeInput))}
                        className="update-time-button"
                      >
                        <span>✓</span> Update Time
                      </button>
                    </div>
                    {updateSuccess && (
                      <div className="success-message">
                        <span>✅</span> Extra time updated successfully!
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            
            {/* Performance Summary */}
            <div className="profile-card performance-summary">
              <div className="card-header">
                <h3 className="card-title">Performance Summary</h3>
                <button 
                  onClick={handleDownloadCSV} 
                  className="download-button"
                  title="Download assessment data as CSV"
                >
                  <span className="download-icon">📊</span>
                  <span>Download Data</span>
                </button>
              </div>
              
              {stats.totalQuizzes > 0 ? (
                <div className="stats-grid">
                  <div className="stat-item">
                    <div className="stat-value">{stats.totalQuizzes}</div>
                    <div className="stat-label">Quizzes Taken</div>
                  </div>
                  <div className="stat-item">
                    <div className={`stat-value ${getScoreClass(parseFloat(stats.averageScore))}`}>
                      {stats.averageScore}%
                    </div>
                    <div className="stat-label">Average Score</div>
                  </div>
                  <div className="stat-item">
                    <div className="stat-value high">{stats.highestScore}%</div>
                    <div className="stat-label">Highest Score</div>
                  </div>
                  <div className="stat-item">
                    <div className="stat-value">{stats.totalSubmissions}</div>
                    <div className="stat-label">Total Submissions</div>
                  </div>
                </div>
              ) : (
                <div className="empty-stats">
                  <p>This student has not completed any quizzes for your class yet.</p>
                </div>
              )}
            </div>
            
            {/* AI Analysis Insights */}
            <div className="profile-card ai-insights">
              <h3 className="card-title">AI Analysis Insights</h3>
              
              {submissions.length > 0 ? (
                <div className="insights-container">
                  <div className="insight-section">
                    <h4 className="insight-title">Strengths</h4>
                    <ul className="insight-list">
                      {insights.strengths.map((strength, index) => (
                        <li key={index} className="insight-item strength">{strength}</li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="insight-section">
                    <h4 className="insight-title">Areas for Improvement</h4>
                    <ul className="insight-list">
                      {insights.weaknesses.map((weakness, index) => (
                        <li key={index} className="insight-item weakness">{weakness}</li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="insight-section">
                    <h4 className="insight-title">Teaching Suggestions</h4>
                    <ul className="insight-list">
                      {insights.suggestions.map((suggestion, index) => (
                        <li key={index} className="insight-item suggestion">{suggestion}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              ) : (
                <div className="empty-insights">
                  <p>No data available for AI analysis yet.</p>
                </div>
              )}
            </div>
            
            {/* Quiz History */}
            <div className="profile-card quiz-history">
              <h3 className="card-title">Quiz History</h3>
              
              {Object.keys(quizzes).length > 0 ? (
                <div className="quizzes-list">
                  {Object.values(quizzes).map((quiz) => {
                    const percentage = quiz.maxMarks > 0 
                      ? ((quiz.totalMarks / quiz.maxMarks) * 100).toFixed(2) 
                      : "N/A";
                    const scoreClass = getScoreClass(parseFloat(percentage));
                    const isExpanded = expandedQuizCode === quiz.code;
                    
                    return (
                      <div key={quiz.code} className="quiz-item">
                        <div 
                          className="quiz-header-row"
                          onClick={() => toggleExpand(quiz.code)}
                        >
                          <div className="quiz-info">
                            <h4 className="quiz-code">{quiz.code}</h4>
                            <span className="quiz-date">Last submission: {formatDate(quiz.lastSubmitted)}</span>
                          </div>
                          <div className="quiz-score-container">
                            <span className={`quiz-score ${scoreClass}`}>
                              {quiz.totalMarks}/{quiz.maxMarks} ({percentage}%)
                            </span>
                            <span className="expand-icon">{isExpanded ? '▼' : '►'}</span>
                          </div>
                        </div>
                        
                        {isExpanded && submissionsByQuiz[quiz.code] && (
                          <div className="quiz-details">
                            <table className="submissions-table">
                              <thead>
                                <tr>
                                  <th>Question</th>
                                  <th>Answer</th>
                                  <th>Feedback</th>
                                  <th>Score</th>
                                </tr>
                              </thead>
                              <tbody>
                                {submissionsByQuiz[quiz.code].map((sub) => (
                                  <tr key={sub.id}>
                                    <td className="question-cell">
                                      <div className="question-text">{sub.questionText}</div>
                                      {sub.questionImageURL && (
                                        <div className="question-image-container">
                                          <img 
                                            src={sub.questionImageURL} 
                                            alt="Question" 
                                            className="question-image"
                                            loading="lazy" // Add lazy loading for images
                                          />
                                        </div>
                                      )}
                                    </td>
                                    <td className="answer-cell">{sub.userAnswer}</td>
                                    <td className="feedback-cell">
                                      {sub.feedback !== "Pending" ? (
                                        <div className="feedback-content">
                                          {formatFeedback(sub.feedback)}
                                        </div>
                                      ) : "Pending"}
                                    </td>
                                    <td className="score-cell">
                                      {sub.markAwarded !== "Pending" ? (
                                        <span className={`score-badge ${parseFloat(sub.markAwarded) === parseFloat(sub.maxMark) 
                                          ? 'full' 
                                          : parseFloat(sub.markAwarded) > 0 ? 'partial' : 'zero'}`}>
                                          {sub.markAwarded}/{sub.maxMark}
                                        </span>
                                      ) : (
                                        <span className="score-badge pending">
                                          Pending
                                        </span>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="empty-quizzes">
                  <p>This student has not completed any quizzes for your class yet.</p>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Footer */}
        <footer className="profile-footer">
          <p>© {new Date().getFullYear()} AI Automarker. All rights reserved.</p>
        </footer>
      </div>
    </div>
  );
};

export default StudentProfile;
